
import { useState, useCallback } from 'react';
import { UserProfile, Organization, UserOrganizationMembership } from './types';
import { 
  fetchUserProfile, 
  fetchOrganization, 
  fetchUserOrganizations, 
  updateLastLogin 
} from './userDataService';

export const useUserData = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [userMemberships, setUserMemberships] = useState<UserOrganizationMembership[]>([]);
  const [currentUserRole, setCurrentUserRole] = useState<'admin' | 'manager' | 'staff' | null>(null);

  const switchToOrganization = useCallback(async (organizationId: string, memberships?: UserOrganizationMembership[]) => {
    try {
      console.log('useUserData: Switching to organization:', organizationId);
      
      const currentMemberships = memberships || userMemberships;
      const membership = currentMemberships.find(m => m.organization_id === organizationId);
      
      if (!membership) {
        console.error('useUserData: User is not a member of this organization');
        return;
      }

      // Fetch organization details (bypass cache to ensure fresh notes/settings)
      const orgData = await fetchOrganization(organizationId, true);
      if (!orgData) {
        console.error('useUserData: Organization not found');
        return;
      }

      setCurrentOrganization(orgData);
      setCurrentUserRole(membership.user_role);
      
      // Store preference
      localStorage.setItem('currentOrganizationId', organizationId);
      
      console.log('useUserData: Successfully switched to organization:', orgData.name, 'with role:', membership.user_role);
    } catch (error) {
      console.error('useUserData: Error switching organization:', error);
    }
  }, [userMemberships]); 

  const fetchUserData = useCallback(async (userId: string) => {
    try {
      console.log('useUserData: Fetching user data for:', userId);
      
      // Fetch user profile
      const profileData = await fetchUserProfile(userId);
      if (!profileData) {
        console.error('useUserData: No profile data found');
        return;
      }

      setProfile(profileData);

      // Fetch user organizations
      const memberships = await fetchUserOrganizations(userId);
      console.log('useUserData: User memberships:', memberships);
      setUserMemberships(memberships);

      if (memberships.length === 0) {
        console.error('useUserData: User has no organization memberships');
        return;
      }

      // Set current organization (use stored preference or first organization)
      const storedOrgId = localStorage.getItem('currentOrganizationId');
      let targetOrgId = storedOrgId;
      
      // Check if stored org is valid for this user
      if (!targetOrgId || !memberships.find(m => m.organization_id === targetOrgId)) {
        targetOrgId = memberships[0]?.organization_id;
        console.log('useUserData: Using first available organization:', targetOrgId);
      }

      if (targetOrgId) {
        await switchToOrganization(targetOrgId, memberships);
      }

      // Update last login
      await updateLastLogin(userId);

    } catch (error) {
      console.error('useUserData: Error fetching user data:', error);
    }
  }, []);

  const clearUserData = useCallback(() => {
    console.log('useUserData: Clearing user data');
    setProfile(null);
    setCurrentOrganization(null);
    setUserMemberships([]);
    setCurrentUserRole(null);
    localStorage.removeItem('currentOrganizationId');
    
    // Clear organization cache when user logs out
    import('@/utils/organizationCache').then(({ organizationCache }) => {
      organizationCache.clear();
    });
  }, []);

  return {
    profile,
    currentOrganization,
    userMemberships,
    currentUserRole,
    fetchUserData,
    switchToOrganization,
    clearUserData,
    setProfile
  };
};
